import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Crosshair, Target, XCircle, Hourglass, Newspaper, Bell, Plus, Trophy,
} from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import { api, fmtRelative } from "@/lib/api";
import { AUREUS } from "@/constants/testIds";

const META = {
  CREATED:        { Icon: Plus,      color: "text-zinc-400", chip: "chip-neutral" },
  ENTERED:        { Icon: Crosshair, color: "text-gold",     chip: "chip-gold"    },
  TP1_HIT:        { Icon: Target,    color: "text-bull",     chip: "chip-bull"    },
  TP2_HIT:        { Icon: Trophy,    color: "text-bull",     chip: "chip-bull"    },
  SL_HIT:         { Icon: XCircle,   color: "text-bear",     chip: "chip-bear"    },
  EXPIRED:        { Icon: Hourglass, color: "text-zinc-500", chip: "chip-neutral" },
  NEWS_HIGH_RISK: { Icon: Newspaper, color: "text-bear",     chip: "chip-bear"    },
};

export default function AlertsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = React.useState("all"); // all | unread
  const { data, isLoading } = useQuery({
    queryKey: ["alerts", filter],
    queryFn: () => api.get("/alerts", { params: { limit: 200, unread_only: filter === "unread" } }).then((r) => r.data),
    refetchInterval: 5000,
  });
  const markRead = useMutation({
    mutationFn: (id) => api.post(`/alerts/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.post("/alerts/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  return (
    <AppShell>
      <div className="p-6 md:p-10" data-testid={AUREUS.alertsPage}>
        <header className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="overline mb-3">Inbox</div>
            <h1 className="heading text-3xl md:text-5xl font-bold tracking-tighter">
              Alerts. <span className="text-gold-grad">Every signal.</span>
            </h1>
            <p className="text-zinc-400 mt-3 text-sm">
              {data?.unread > 0 ? `${data.unread} unread` : "All caught up."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={!data?.unread}
            data-testid={AUREUS.alertsMarkAll}
            className="px-4 py-2 mono text-[11px] uppercase tracking-widest border border-white/10 hover:bg-white/5 transition disabled:opacity-40"
          >
            Mark all read
          </button>
        </header>

        <div className="flex items-center gap-2 mb-4" data-testid={AUREUS.alertsFilters}>
          {["all", "unread"].map((f) => (
            <button
              key={f}
              type="button"
              data-testid={AUREUS.alertsFilterBtn(f)}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest border transition ${
                filter === f
                  ? "border-gold/60 bg-gold/10 text-gold"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {f === "all" ? "All" : `Unread${data?.unread ? ` · ${data.unread}` : ""}`}
            </button>
          ))}
        </div>

        {isLoading && <div className="surface h-96 animate-pulse" />}

        {data?.alerts && (
          <div className="surface">
            {data.alerts.length === 0 ? (
              <div className="p-10 text-center text-zinc-500 text-sm flex flex-col items-center gap-3">
                <Bell size={28} className="text-zinc-700" />
                <span>No alerts yet — they'll appear here as setups + news fire.</span>
              </div>
            ) : data.alerts.map((a) => {
              const m = META[a.event_type] || META.CREATED;
              return (
                <div
                  key={a.id}
                  data-testid={AUREUS.alertRow(a.id)}
                  className={`flex items-center gap-4 px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition ${a.read ? "opacity-60" : ""}`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center border border-white/10 ${m.color}`}>
                    <m.Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="heading text-sm font-semibold">{a.title}</span>
                      {!a.read && <span className="chip chip-gold">NEW</span>}
                      {a.symbol && <span className="chip chip-neutral">{a.symbol}</span>}
                    </div>
                    <div className="text-sm text-zinc-300 mt-1 truncate">{a.body}</div>
                  </div>
                  <div className="mono text-xs text-zinc-500 whitespace-nowrap">{fmtRelative(a.created_at)}</div>
                  {!a.read && (
                    <button
                      type="button"
                      onClick={() => markRead.mutate(a.id)}
                      className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
                    >
                      Read
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
